/**
 * Side-by-side compare dialog (FEAT-2026-0005, Phase 2 + Phase 4).
 *
 * Three tabs:
 *   - Rendered PDFs   — two react-pdf viewers side-by-side; renders both
 *                       versions against the active sample WOD so the user
 *                       can SEE the visual difference, not just read JSON.
 *                       Synchronized page navigation + zoom.
 *   - JSON diff       — react-diff-viewer-continued (split view, word-level)
 *   - Changed fields  — recursive walker showing only the leaf paths whose
 *                       values changed, with from/to side-by-side
 *
 * The PDF tab is the default — it's what you visit Compare for.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Tabs, Tab, Box, Stack, Chip, Typography, IconButton, Alert,
  Skeleton, Select, MenuItem,
} from '@mui/material';
import { Close, ZoomIn, ZoomOut, NavigateBefore, NavigateNext } from '@mui/icons-material';
import ReactDiffViewer, { DiffMethod } from 'react-diff-viewer-continued';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import {
  reportTemplateService,
  type VersionWithMetadata,
  type PreviewSampleRow,
  formatSampleOption,
} from '../../../services/reportTemplateService';
import { useReportTemplateStore } from './useReportTemplateStore';

// pdfjs worker — same setup the PreviewPane uses; setting it twice is safe
// because react-pdf de-dupes globally.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/build/pdf.worker.min.mjs',
  import.meta.url,
).toString();

interface Props {
  open: boolean;
  onClose: () => void;
  templateKey: string;
  versionA: number | null;   // older
  versionB: number | null;   // newer / current
  /**
   * Optional unsaved draft config — when provided, replaces the loaded
   * `versionB` config on the right side of every comparison. Used by the
   * toolbar's "Compare" button (when there are unsaved edits, the user
   * wants to see the IN-MEMORY draft vs the active saved version, not the
   * meaningless v1 vs v1 the dialog would otherwise show).
   */
  draftConfig?: unknown | null;
}

type CompareTab = 'pdfs' | 'diff' | 'summary';

/**
 * Recursively walk two configs and collect leaf paths whose values differ.
 * Used to power the "changed fields" summary tab.
 */
function diffFields(a: any, b: any, prefix = ''): { path: string; from: unknown; to: unknown }[] {
  const out: { path: string; from: unknown; to: unknown }[] = [];
  const aIsObj = a && typeof a === 'object' && !Array.isArray(a);
  const bIsObj = b && typeof b === 'object' && !Array.isArray(b);
  if (aIsObj && bIsObj) {
    const keys = new Set<string>([...Object.keys(a), ...Object.keys(b)]);
    for (const k of keys) {
      out.push(...diffFields(a[k], b[k], prefix ? `${prefix}.${k}` : k));
    }
  } else if (JSON.stringify(a) !== JSON.stringify(b)) {
    out.push({ path: prefix || '<root>', from: a, to: b });
  }
  return out;
}

export const CompareDialog = ({ open, onClose, templateKey, versionA, versionB, draftConfig }: Props) => {
  const [tab, setTab] = useState<CompareTab>('pdfs');
  const [a, setA] = useState<VersionWithMetadata | null>(null);
  const [b, setB] = useState<VersionWithMetadata | null>(null);
  const [error, setError] = useState<string | null>(null);

  const isDraftMode = draftConfig != null;
  // In draft mode the "newer" side IS the in-memory draft. Build a synthetic
  // version-with-metadata for it so all the downstream rendering paths can
  // treat both sides uniformly.
  const draftSide: VersionWithMetadata = useMemo(() => ({
    version: {
      ReportTemplateVersionKey: 'draft',
      ReportTemplateKey: templateKey,
      VersionNumber: -1,
      Config: JSON.stringify(draftConfig ?? {}),
      ConfigHash: '',
      ChangeNote: 'Unsaved edits in the editor',
      ParentVersionKey: null,
      PublishedAt: new Date().toISOString(),
      PublishedBy: '(you)',
      IsFactory: false,
    },
    parsedConfig: draftConfig,
    isValid: true,
  }), [draftConfig, templateKey]);

  // Load the version pair when the dialog opens. In draft mode we only load
  // the LEFT side (the saved current version) — the right side is the
  // in-memory draft.
  useEffect(() => {
    if (!open || versionA == null || versionB == null) return;
    let cancelled = false;
    setError(null);
    if (isDraftMode) {
      // Just load the saved version for the left side.
      reportTemplateService.getVersion(templateKey, versionA)
        .then((v) => { if (!cancelled) { setA(v); setB(draftSide); } })
        .catch((err) => { if (!cancelled) setError(err?.message ?? String(err)); });
    } else {
      reportTemplateService.getDiff(templateKey, versionA, versionB)
        .then((d) => { if (!cancelled) { setA(d.a); setB(d.b); } })
        .catch((err) => { if (!cancelled) setError(err?.message ?? String(err)); });
    }
    return () => { cancelled = true; };
  }, [open, templateKey, versionA, versionB, isDraftMode, draftSide]);

  const aText = useMemo(() => a ? JSON.stringify(a.parsedConfig, null, 2) : '', [a]);
  const bText = useMemo(() => b ? JSON.stringify(b.parsedConfig, null, 2) : '', [b]);
  const changes = useMemo(() => (a && b) ? diffFields(a.parsedConfig, b.parsedConfig) : [], [a, b]);
  // Display labels for the two sides of the comparison.
  const labelA = `v${versionA}`;
  const labelB = isDraftMode ? 'draft' : `v${versionB}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth
      PaperProps={{ sx: { height: '92vh' } }}>
      <DialogTitle sx={{ pr: 6, py: 1.5 }}>
        Compare versions
        <Stack direction="row" spacing={1} sx={{ mt: 0.5 }}>
          <Chip size="small" label={labelA} variant="outlined" />
          <Typography variant="body2" sx={{ alignSelf: 'center', color: 'text.secondary' }}>↔</Typography>
          <Chip size="small" label={labelB} color={isDraftMode ? 'warning' : 'primary'} />
          {changes.length > 0 && (
            <Chip size="small"
              label={`${changes.length} ${changes.length === 1 ? 'field' : 'fields'} changed`}
              color="warning" variant="outlined"
            />
          )}
        </Stack>
        <IconButton onClick={onClose} size="small" sx={{ position: 'absolute', top: 12, right: 12 }}>
          <Close fontSize="small" />
        </IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_e, v) => setTab(v)} sx={{ borderBottom: 1, borderColor: 'divider', px: 3 }}>
        <Tab label="Rendered PDFs" value="pdfs" />
        <Tab label="JSON diff" value="diff" />
        <Tab label={`Changed fields (${changes.length})`} value="summary" />
      </Tabs>
      <DialogContent dividers sx={{ p: 0, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        {error && <Alert severity="error" sx={{ m: 2 }}>{error}</Alert>}

        {tab === 'pdfs' && (
          <PdfComparePane
            templateKey={templateKey}
            labelA={labelA}
            labelB={labelB}
            configA={a?.parsedConfig}
            configB={b?.parsedConfig}
          />
        )}

        {tab === 'diff' && (
          <Box sx={{
            '& *': { fontFamily: 'monospace !important' },
            fontSize: '0.85rem',
            overflow: 'auto', flex: 1,
          }}>
            <ReactDiffViewer
              oldValue={aText}
              newValue={bText}
              splitView
              compareMethod={DiffMethod.WORDS}
              leftTitle={labelA}
              rightTitle={labelB}
              styles={{
                contentText: { fontSize: 12, lineHeight: '1.4em' },
                gutter: { padding: '0 4px' },
              }}
            />
          </Box>
        )}

        {tab === 'summary' && (
          <Box sx={{ p: 3, overflow: 'auto', flex: 1 }}>
            {changes.length === 0 ? (
              <Typography color="text.secondary">No differences.</Typography>
            ) : (
              <Stack spacing={1.5}>
                {changes.map((c) => (
                  <Box key={c.path} sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
                      {c.path}
                    </Typography>
                    <Stack direction="row" spacing={2} sx={{ mt: 0.5 }}>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="error.main">{labelA}</Typography>
                        <Box component="pre" sx={{ m: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(c.from)}
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Typography variant="caption" color="success.main">{labelB}</Typography>
                        <Box component="pre" sx={{ m: 0, fontSize: '0.8rem', whiteSpace: 'pre-wrap' }}>
                          {JSON.stringify(c.to)}
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                ))}
              </Stack>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

// ── PDF compare pane ────────────────────────────────────────────────────────
//
// Two react-pdf viewers side-by-side. Page navigation and zoom are mirrored
// across both viewers so you can step through a multi-page report and watch
// the same page render with each version's config.

interface PdfComparePaneProps {
  templateKey: string;
  labelA: string;
  labelB: string;
  configA: unknown | undefined;
  configB: unknown | undefined;
}

const PdfComparePane = ({ templateKey, labelA, labelB, configA, configB }: PdfComparePaneProps) => {
  const previewWodKey = useReportTemplateStore((s) => s.previewWorkOrderDetailKey);
  const setPreviewWodKey = useReportTemplateStore((s) => s.setPreviewWorkOrderDetailKey);

  const [samples, setSamples] = useState<PreviewSampleRow[]>([]);
  const [pdfA, setPdfA] = useState<string | null>(null);
  const [pdfB, setPdfB] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [renderError, setRenderError] = useState<string | null>(null);
  const [pageIndex, setPageIndex] = useState(0);
  const [numPages, setNumPages] = useState<number>(0);
  const [zoom, setZoom] = useState(0.7);

  // Track blob URLs so we can revoke them on the next render to avoid
  // leaking memory on repeated compares.
  const aUrlRef = useRef<string | null>(null);
  const bUrlRef = useRef<string | null>(null);

  // Load curated samples on mount; default to the first if the user
  // hasn't picked one yet.
  useEffect(() => {
    let cancelled = false;
    reportTemplateService.listSamples(templateKey)
      .then((rows) => {
        if (cancelled) return;
        setSamples(rows);
        if (!previewWodKey && rows.length > 0) setPreviewWodKey(rows[0].WorkOrderDetailKey);
      })
      .catch((err) => { if (!cancelled) setRenderError(err?.message ?? String(err)); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templateKey]);

  // Render both PDFs in parallel whenever the active sample / configs change.
  useEffect(() => {
    if (!previewWodKey || configA == null || configB == null) return;
    let cancelled = false;
    setLoading(true);
    setRenderError(null);
    Promise.all([
      reportTemplateService.renderPreview(templateKey, { config: configA, workOrderDetailKey: previewWodKey }),
      reportTemplateService.renderPreview(templateKey, { config: configB, workOrderDetailKey: previewWodKey }),
    ])
      .then(([blobA, blobB]) => {
        if (cancelled) return;
        const urlA = URL.createObjectURL(blobA);
        const urlB = URL.createObjectURL(blobB);
        if (aUrlRef.current) URL.revokeObjectURL(aUrlRef.current);
        if (bUrlRef.current) URL.revokeObjectURL(bUrlRef.current);
        aUrlRef.current = urlA;
        bUrlRef.current = urlB;
        setPdfA(urlA);
        setPdfB(urlB);
      })
      .catch((err: any) => {
        if (cancelled) return;
        const detail = err?.response?.data?.error ?? err?.message ?? String(err);
        setRenderError(detail);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [previewWodKey, configA, configB, templateKey]);

  // Cleanup blob URLs on unmount.
  useEffect(() => () => {
    if (aUrlRef.current) URL.revokeObjectURL(aUrlRef.current);
    if (bUrlRef.current) URL.revokeObjectURL(bUrlRef.current);
  }, []);

  const onLoadSuccess = ({ numPages: n }: { numPages: number }) => {
    setNumPages((cur) => Math.max(cur, n));
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, minWidth: 0 }}>
      {/* Toolbar — sample picker + zoom + page nav, applied to both viewers. */}
      <Stack direction="row" spacing={1} alignItems="center" sx={{ p: 1.5, borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
        <Typography variant="caption" sx={{ minWidth: 56 }}>Sample:</Typography>
        <Select
          size="small"
          value={previewWodKey ?? ''}
          onChange={(e) => setPreviewWodKey(e.target.value || null)}
          sx={{ minWidth: 180, fontSize: '0.85rem' }}
          displayEmpty
        >
          <MenuItem value="" disabled>Choose a sample…</MenuItem>
          {samples.map((s) => (
            <MenuItem key={s.WorkOrderDetailKey} value={s.WorkOrderDetailKey}>
              {formatSampleOption(s, samples)}
            </MenuItem>
          ))}
        </Select>
        <Box sx={{ flex: 1 }} />
        <IconButton size="small" onClick={() => setZoom((z) => Math.max(0.3, z - 0.1))}><ZoomOut fontSize="small" /></IconButton>
        <Typography variant="caption" sx={{ minWidth: 40, textAlign: 'center' }}>{Math.round(zoom * 100)}%</Typography>
        <IconButton size="small" onClick={() => setZoom((z) => Math.min(2.0, z + 0.1))}><ZoomIn fontSize="small" /></IconButton>
        {numPages > 1 && (
          <>
            <IconButton size="small" disabled={pageIndex === 0} onClick={() => setPageIndex((i) => Math.max(0, i - 1))}>
              <NavigateBefore fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ minWidth: 60, textAlign: 'center' }}>
              {pageIndex + 1} / {numPages}
            </Typography>
            <IconButton size="small" disabled={pageIndex >= numPages - 1} onClick={() => setPageIndex((i) => Math.min(numPages - 1, i + 1))}>
              <NavigateNext fontSize="small" />
            </IconButton>
          </>
        )}
      </Stack>

      {/* Two-pane grid — explicit 50/50 columns + minWidth:0 on each cell so
          the inner PDF can never blow out the layout. minHeight on the wrapper
          keeps each pane's overflow:auto contained. */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '1fr 1px 1fr',
        flex: 1, minHeight: 0, minWidth: 0,
      }}>
        <PdfPane
          title={labelA}
          color="error.main"
          url={pdfA}
          loading={loading}
          pageIndex={pageIndex}
          zoom={zoom}
          onLoadSuccess={onLoadSuccess}
          onError={(m) => setRenderError(m)}
        />
        <Box sx={{ bgcolor: 'divider' }} />
        <PdfPane
          title={labelB}
          color="success.main"
          url={pdfB}
          loading={loading}
          pageIndex={pageIndex}
          zoom={zoom}
          onLoadSuccess={onLoadSuccess}
          onError={(m) => setRenderError(m)}
        />
      </Box>

      {renderError && <Alert severity="error" sx={{ m: 1 }}>{renderError}</Alert>}
    </Box>
  );
};

// ── Single PDF pane (left or right of the comparison) ───────────────────────
//
// Uses a ResizeObserver to track its own width and renders the PDF page with
// an explicit `width` prop based on `(observed width) × zoom`. This is the
// only reliable way to constrain the page to the pane — `scale` alone uses
// the PDF's intrinsic 612-pt dimensions, which can blow out the layout when
// the pane is narrower than 612 × scale.

interface PdfPaneProps {
  title: string;
  color: string;
  url: string | null;
  loading: boolean;
  pageIndex: number;
  zoom: number;
  onLoadSuccess: (args: { numPages: number }) => void;
  onError: (msg: string) => void;
}

const PdfPane = ({ title, color, url, loading, pageIndex, zoom, onLoadSuccess, onError }: PdfPaneProps) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [paneWidth, setPaneWidth] = useState<number>(400);

  useEffect(() => {
    if (!containerRef.current) return;
    const el = containerRef.current;
    const ro = new ResizeObserver(() => {
      // Subtract horizontal padding (p:2 = 16px each side = 32 px) so the
      // rendered page doesn't quite touch the pane edges.
      setPaneWidth(Math.max(120, el.clientWidth - 32));
    });
    ro.observe(el);
    setPaneWidth(Math.max(120, el.clientWidth - 32));
    return () => ro.disconnect();
  }, []);

  // The desired render width: pane width × user zoom. Scrollbars handle the
  // overflow when zoom > ~1.0 makes the page wider than the pane.
  const renderWidth = Math.max(80, Math.round(paneWidth * zoom));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 0, minHeight: 0, overflow: 'hidden' }}>
      <Box sx={{ p: 0.75, borderBottom: 1, borderColor: 'divider', textAlign: 'center', flexShrink: 0 }}>
        <Typography variant="caption" sx={{ fontWeight: 600, color }}>
          {title}
        </Typography>
      </Box>
      <Box
        ref={containerRef}
        sx={{
          flex: 1, overflow: 'auto', minWidth: 0, minHeight: 0,
          p: 2, bgcolor: 'grey.100',
          // Centre the page within the pane WITHOUT flex (which can clip with
          // narrow content). margin: auto on the inner Document handles it.
          '& .react-pdf__Document': { display: 'block', width: 'fit-content', mx: 'auto' },
          '& .react-pdf__Page': { boxShadow: 1, mb: 1 },
          '& canvas': { display: 'block', maxWidth: '100% !important', height: 'auto !important' },
        }}
      >
        {loading && <Skeleton variant="rectangular" sx={{ mx: 'auto', width: renderWidth, height: renderWidth * 1.294 }} />}
        {!loading && url && (
          <Document
            file={url}
            onLoadSuccess={onLoadSuccess}
            onLoadError={(err) => onError(err?.message ?? 'PDF load error')}
            loading={<Skeleton variant="rectangular" sx={{ mx: 'auto', width: renderWidth, height: renderWidth * 1.294 }} />}
          >
            <Page
              pageIndex={pageIndex}
              width={renderWidth}
              renderAnnotationLayer={false}
              renderTextLayer={false}
            />
          </Document>
        )}
      </Box>
    </Box>
  );
};
